import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Check, ArrowLeft, ArrowRight, UploadCloud, Loader2, FileCheck2, X, CloudCheck, Send, Clock, CheckCircle2 } from "lucide-react";
import { formatError } from "@/lib/api";
import applicantApi from "@/lib/applicantApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { UNIVERSITIES, FIELDS_OF_STUDY, HOW_HEARD, DOCUMENT_FIELDS, ACADEMIC_STRUCTURE } from "@/data/formConfig";
import { valueLabel, fmtDate, fmtDateTime, fmtTime } from "@/i18n";

const Field = ({ label, required, children, hint }) => (
  <div className="flex flex-col">
    {label && (
      <Label className="text-sm font-medium mb-2 block">
        {label} {required && <span className="text-accent">*</span>}
      </Label>
    )}
    {hint && <p className="text-xs text-muted-foreground mb-2">{hint}</p>}
    {children}
  </div>
);

const RadioField = ({ label, required, options, value, onChange, testid }) => (
  <Field label={label} required={required}>
    <RadioGroup value={value} onValueChange={onChange} className="flex flex-wrap gap-3" data-testid={testid}>
      {options.map((o) => (
        <label key={o} className={`flex items-center gap-2 border rounded-lg px-4 py-2.5 cursor-pointer transition-colors ${value === o ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}>
          <RadioGroupItem value={o} />
          <span className="text-sm">{valueLabel(o)}</span>
        </label>
      ))}
    </RadioGroup>
  </Field>
);

function FileUploadField({ doc, files, setFiles, bare }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const current = files.filter((f) => f.field === doc.key);
  const isMultiple = !!doc.multiple;
  const showDropzone = isMultiple || current.length === 0;

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("errors.file_too_large"));
      e.target.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("field", doc.key);
    fd.append("file", file);
    try {
      const res = await applicantApi.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((prev) => {
        const others = isMultiple ? prev : prev.filter((f) => f.field !== doc.key);
        return [...others, res.data];
      });
      toast.success(t("form.upload.uploaded", { name: file.name }));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("errors.upload_failed"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const openFile = async (f) => {
    try {
      const res = await applicantApi.get(`/applicant/files/${f.storage_path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch {
      toast.error(t("errors.could_not_open_document"));
    }
  };

  return (
    <Field label={bare ? null : t(`documents.${doc.key}.label`)} required={doc.required} hint={bare ? null : t(`documents.${doc.key}.hint`)}>
      {current.length > 0 && (
        <div className="space-y-2 mb-3">
          {current.map((f) => (
            <div key={f.id} className="flex items-center justify-between bg-secondary/60 border border-primary/10 rounded-lg px-3 py-2 text-sm">
              <button type="button" onClick={() => openFile(f)} className="flex items-center gap-2 truncate hover:underline text-left" title={t("form.upload.open")}>
                <FileCheck2 className="w-4 h-4 text-primary shrink-0" /> {f.original_filename}
              </button>
              <button type="button" onClick={() => removeFile(f.id)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-file-${doc.key}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      {showDropzone && (
        <label className="border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors" data-testid={`upload-${doc.key}`}>
          {uploading ? <Loader2 className="w-6 h-6 text-primary animate-spin" /> : <UploadCloud className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />}
          <span className="text-sm text-muted-foreground">{uploading ? t("common.uploading") : (isMultiple && current.length > 0 ? t("form.upload.add_another") : t("form.upload.click"))}</span>
          {!uploading && <span className="text-xs text-muted-foreground text-center max-w-md" data-testid={`scan-tip-${doc.key}`}>{t("form.upload.scan_tip")}</span>}
          <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" />
        </label>
      )}
    </Field>
  );
}

const REFERENCE_SLOTS = ["recommendation_1", "recommendation_2"];

// A recommendation letter: uploaded by the applicant, or requested from the referee by email.
function RecommendationField({ doc, files, setFiles, reference, setReferences }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(reference ? "ask" : "upload");
  const [editing, setEditing] = useState(!reference);
  const [ref, setRef] = useState({ referee_name: reference?.referee_name || "", referee_email: reference?.referee_email || "", message: "" });
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!ref.referee_name.trim() || !ref.referee_email.trim()) { toast.error(t("form.reference.need_name_email")); return; }
    setBusy(true);
    try {
      const res = await applicantApi.put(`/applicant/references/${doc.key}`, ref);
      setReferences(res.data.references);
      setEditing(false);
      toast.success(t("form.reference.request_sent", { email: ref.referee_email }));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("form.reference.could_not_send"));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await applicantApi.delete(`/applicant/references/${doc.key}`);
      setReferences(res.data.references);
      setRef({ referee_name: "", referee_email: "", message: "" });
      setEditing(true);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("form.reference.could_not_cancel"));
    } finally {
      setBusy(false);
    }
  };

  const received = reference?.status === "received";
  const tab = (key, label) => (
    <button type="button" onClick={() => setMode(key)} disabled={received && key === "upload"}
      className={`flex-1 text-sm px-3 py-2 rounded-md transition-colors ${mode === key ? "bg-card shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
      data-testid={`${doc.key}-mode-${key}`}>{label}</button>
  );

  return (
    <div className="flex flex-col">
      <Label className="text-sm font-medium mb-2 block">
        {t(`documents.${doc.key}.label`)} {doc.required && <span className="text-accent">*</span>}
      </Label>
      {t(`documents.${doc.key}.hint`) && <p className="text-xs text-muted-foreground mb-2">{t(`documents.${doc.key}.hint`)}</p>}
      {!received && (
        <div className="flex gap-1 bg-muted rounded-lg p-1 mb-3">
          {tab("upload", t("form.reference.mode_upload"))}
          {tab("ask", t("form.reference.mode_ask"))}
        </div>
      )}

      {mode === "upload" && !received && <FileUploadField doc={doc} files={files} setFiles={setFiles} bare />}

      {(mode === "ask" || received) && (
        reference && !editing ? (
          <div className="border border-border rounded-xl p-4 text-sm" data-testid={`${doc.key}-request`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium">{reference.referee_name}</p>
                <p className="text-muted-foreground">{reference.referee_email}</p>
              </div>
              {received ? (
                <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2.5 py-1"><CheckCircle2 className="w-3.5 h-3.5" /> {t("form.reference.received")}</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs bg-muted rounded-full px-2.5 py-1"><Clock className="w-3.5 h-3.5" /> {t("form.reference.waiting")}</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              {received
                ? t("form.reference.received_text", { date: fmtDate(reference.received_at) })
                : t("form.reference.sent_text", { date: fmtDateTime(reference.sent_at) })}
            </p>
            {!received && (
              <div className="flex flex-wrap gap-2 mt-3">
                <Button type="button" size="sm" variant="outline" disabled={busy} onClick={send} data-testid={`${doc.key}-resend`}>{t("form.reference.resend")}</Button>
                <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(true)}>{t("form.reference.change")}</Button>
                <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={cancel} className="text-muted-foreground hover:text-destructive" data-testid={`${doc.key}-cancel`}>{t("form.reference.cancel")}</Button>
              </div>
            )}
          </div>
        ) : (
          <div className="border border-border rounded-xl p-4 grid gap-3" data-testid={`${doc.key}-request-form`}>
            <p className="text-xs text-muted-foreground">{t("form.reference.explain")}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input placeholder={t("form.reference.name_placeholder")} value={ref.referee_name} onChange={(e) => setRef({ ...ref, referee_name: e.target.value })} className="h-11" data-testid={`${doc.key}-referee-name`} />
              <Input type="email" placeholder={t("form.reference.email_placeholder")} value={ref.referee_email} onChange={(e) => setRef({ ...ref, referee_email: e.target.value })} className="h-11" data-testid={`${doc.key}-referee-email`} />
            </div>
            <Textarea placeholder={t("form.reference.note_placeholder")} rows={2} value={ref.message} onChange={(e) => setRef({ ...ref, message: e.target.value })} />
            <div className="flex gap-2">
              <Button type="button" size="sm" onClick={send} disabled={busy} data-testid={`${doc.key}-send-request`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> {t("form.reference.send")}</>}
              </Button>
              {reference && <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>{t("common.back")}</Button>}
            </div>
          </div>
        )
      )}
    </div>
  );
}

const EMPTY_FORM = {
  read_preliminaries: "", email: "", name: "", surname: "", address: "", city: "",
  country_of_origin: "", residence_permit: "", phone: "",
  university: "", faculty: "", study_program: "", field_of_study: "", degree_level: "",
  semesters_studied: "", program_duration: "", remaining_exams: "", gpa: "",
  financial_support: "", financial_support_institution: "", previous_penda: "",
  parents_marital_status: "", siblings: "", parent_unemployed: "", monthly_income: "", vehicles: "",
  additional_notes: "", how_heard: [], consent: "",
};

// Start from saved answers (draft or submitted application), keeping only known fields.
const fromSaved = (saved, email) => {
  const out = { ...EMPTY_FORM };
  for (const k of Object.keys(EMPTY_FORM)) if (saved?.[k] != null) out[k] = saved[k];
  out.email = email;
  return out;
};

const AUTOSAVE_MS = 1500;

/**
 * The 5-step scholarship form.
 *   mode "draft": answers autosave as a draft; the last step submits.
 *   mode "edit":  editing a submitted application; the last step saves changes.
 */
export default function ApplicationForm({ mode, email, initial, references: initialReferences, onDone, onCancel }) {
  const { t } = useTranslation();
  const STEPS = t("form.steps", { returnObjects: true });
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState(initial?.files || []);
  const [form, setForm] = useState(() => fromSaved(initial, email));
  const [savedAt, setSavedAt] = useState(initial?.saved_at || null);
  const [saving, setSaving] = useState(false);
  const [references, setReferences] = useState(initialReferences || []);
  const referenceFor = (key) => references.find((r) => r.slot === key);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Cascading LoV state for Field of Study -> Faculty -> Program.
  // Saved values that aren't in the lists were typed in via "Other".
  const savedFaculties = Object.keys(ACADEMIC_STRUCTURE[form.field_of_study] || {});
  const [facultyOther, setFacultyOther] = useState(!!form.faculty && !savedFaculties.includes(form.faculty));
  const [programOther, setProgramOther] = useState(
    !!form.study_program && !(ACADEMIC_STRUCTURE[form.field_of_study]?.[form.faculty] || []).includes(form.study_program));

  // Draft autosave (debounced); skipped for the initial render.
  const firstRender = useRef(true);
  useEffect(() => {
    if (mode !== "draft") return;
    if (firstRender.current) { firstRender.current = false; return; }
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        const res = await applicantApi.put("/applicant/draft", { ...form, files });
        setSavedAt(res.data.saved_at);
      } catch {
        // keep quiet; the next change retries, and submit reports real errors
      } finally {
        setSaving(false);
      }
    }, AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [form, files, mode]);

  const facultyOptions = Object.keys(ACADEMIC_STRUCTURE[form.field_of_study] || {});
  const programOptions =
    !facultyOther && form.faculty ? ACADEMIC_STRUCTURE[form.field_of_study]?.[form.faculty] || [] : [];

  const handleFieldChange = (v) => {
    setForm((p) => ({ ...p, field_of_study: v, faculty: "", study_program: "" }));
    setFacultyOther(false);
    setProgramOther(false);
  };
  const handleFacultyChange = (v) => {
    if (v === "__other__") {
      setFacultyOther(true);
      setForm((p) => ({ ...p, faculty: "", study_program: "" }));
      setProgramOther(true); // no predefined programs for a custom faculty
    } else {
      setFacultyOther(false);
      setProgramOther(false);
      setForm((p) => ({ ...p, faculty: v, study_program: "" }));
    }
  };
  const handleProgramChange = (v) => {
    if (v === "__other__") {
      setProgramOther(true);
      setForm((p) => ({ ...p, study_program: "" }));
    } else {
      setProgramOther(false);
      setForm((p) => ({ ...p, study_program: v }));
    }
  };
  const toggleHeard = (o) =>
    setForm((p) => ({ ...p, how_heard: p.how_heard.includes(o) ? p.how_heard.filter((x) => x !== o) : [...p.how_heard, o] }));

  const isYes = (v) => (v || "").trim().toLowerCase().startsWith("y");

  const firstMissing = (keys) => {
    const key = keys.find((k) => !String(form[k] ?? "").trim());
    return key ? t(`fields.${key}`) : null;
  };
  const missingToast = (field) => toast.error(t("form.please_complete", { field }));

  const validateStep = () => {
    if (step === 0) {
      const missing = firstMissing(["read_preliminaries", "email", "name", "surname", "address", "city",
        "country_of_origin", "residence_permit", "phone"]);
      if (missing) { missingToast(missing); return false; }
    }
    if (step === 1) {
      const missing = firstMissing(["university", "faculty", "study_program", "field_of_study", "degree_level",
        "semesters_studied", "program_duration", "remaining_exams", "gpa", "financial_support", "previous_penda"]);
      if (missing) { missingToast(missing); return false; }
      if (isYes(form.financial_support) && !form.financial_support_institution.trim()) {
        toast.error(t("form.specify_institution")); return false;
      }
    }
    if (step === 2) {
      const missing = firstMissing(["parents_marital_status", "siblings", "parent_unemployed", "monthly_income", "vehicles"]);
      if (missing) { missingToast(missing); return false; }
    }
    if (step === 3) {
      // a recommendation counts once it is uploaded or requested from the referee
      const missingDoc = DOCUMENT_FIELDS.find((d) => d.required && !files.some((f) => f.field === d.key) && !referenceFor(d.key));
      if (missingDoc) { toast.error(t("form.please_upload", { document: t(`documents.${missingDoc.key}.label`) })); return false; }
    }
    if (step === 4) {
      if (form.how_heard.length === 0) { toast.error(t("form.select_how_heard")); return false; }
      if (form.consent !== "I agree") { toast.error(t("form.must_consent")); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = { ...form, files };
      const res = mode === "edit"
        ? await applicantApi.put("/applicant/application", payload)
        : await applicantApi.post("/applicant/submit", payload);
      onDone(res.data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("errors.saving_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
        <div className="flex items-center justify-between gap-4 mb-6 text-sm">
          <span className="text-muted-foreground">
            {mode === "edit" ? t("form.editing_note") : t("form.autosave_note")}
          </span>
          {mode === "draft" && (
            <span className="flex items-center gap-1.5 text-muted-foreground shrink-0" data-testid="draft-status">
              {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("common.saving")}</>
                : savedAt ? <><CloudCheck className="w-4 h-4 text-primary" /> {t("form.draft_saved", { time: fmtTime(savedAt) })}</>
                : null}
            </span>
          )}
          {mode === "edit" && <Button variant="ghost" size="sm" onClick={onCancel} data-testid="cancel-edit-btn">{t("common.cancel")}</Button>}
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-10">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${i < step ? "bg-primary text-primary-foreground" : i === step ? "border-2 border-primary text-primary" : "border border-border text-muted-foreground"}`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-[11px] mt-2 text-center hidden sm:block ${i === step ? "text-primary font-medium" : "text-muted-foreground"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-2 ${i < step ? "bg-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 md:p-10 shadow-sm animate-fade-in">
          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-8">{STEPS[step]}</h2>

          {step === 0 && (
            <div className="grid gap-6">
              <RadioField label={t("form.q.read_preliminaries")} required options={["Yes", "No"]} value={form.read_preliminaries} onChange={(v) => set("read_preliminaries", v)} testid="radio-preliminaries" />
              <Field label={t("fields.email")} required hint={t("form.q.email_hint")}>
                <Input type="email" value={form.email} readOnly disabled data-testid="input-email" className="h-12" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label={t("fields.name")} required><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="input-name" className="h-12" /></Field>
                <Field label={t("fields.surname")} required><Input value={form.surname} onChange={(e) => set("surname", e.target.value)} data-testid="input-surname" className="h-12" /></Field>
              </div>
              <Field label={t("fields.address")} required><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="input-address" className="h-12" /></Field>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label={t("fields.city")} required><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="input-city" className="h-12" /></Field>
                <Field label={t("fields.country_of_origin")} required><Input value={form.country_of_origin} onChange={(e) => set("country_of_origin", e.target.value)} data-testid="input-country" className="h-12" /></Field>
              </div>
              <RadioField label={t("form.q.residence_permit")} required options={["Yes", "No"]} value={form.residence_permit} onChange={(v) => set("residence_permit", v)} testid="radio-residence" />
              <Field label={t("fields.phone")} required><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="input-phone" className="h-12" /></Field>
            </div>
          )}

          {step === 1 && (
            <div className="grid gap-6">
              <Field label={t("fields.university")} required>
                <Select value={form.university} onValueChange={(v) => set("university", v)}>
                  <SelectTrigger className="h-12" data-testid="select-university"><SelectValue placeholder={t("form.q.university_placeholder")} /></SelectTrigger>
                  <SelectContent>{UNIVERSITIES.map((u) => <SelectItem key={u} value={u}>{valueLabel(u)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("fields.field_of_study")} required>
                <Select value={form.field_of_study} onValueChange={handleFieldChange}>
                  <SelectTrigger className="h-12" data-testid="select-field"><SelectValue placeholder={t("form.q.field_placeholder")} /></SelectTrigger>
                  <SelectContent>{FIELDS_OF_STUDY.map((f) => <SelectItem key={f} value={f}>{valueLabel(f)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("fields.faculty")} required hint={!form.field_of_study ? t("form.q.faculty_hint") : undefined}>
                <Select value={facultyOther ? "__other__" : form.faculty} onValueChange={handleFacultyChange} disabled={!form.field_of_study}>
                  <SelectTrigger className="h-12" data-testid="select-faculty"><SelectValue placeholder={t("form.q.faculty_placeholder")} /></SelectTrigger>
                  <SelectContent>
                    {facultyOptions.map((f) => <SelectItem key={f} value={f}>{valueLabel(f)}</SelectItem>)}
                    <SelectItem value="__other__">{t("form.q.other_specify")}</SelectItem>
                  </SelectContent>
                </Select>
                {facultyOther && (
                  <Input value={form.faculty} onChange={(e) => set("faculty", e.target.value)} placeholder={t("form.q.faculty_other")} data-testid="input-faculty-other" className="h-12 mt-3" />
                )}
              </Field>
              <Field label={t("form.q.program")} required hint={!form.faculty && !facultyOther ? t("form.q.program_hint") : undefined}>
                {programOther ? (
                  <Input value={form.study_program} onChange={(e) => set("study_program", e.target.value)} placeholder={t("form.q.program_other")} data-testid="input-program-other" className="h-12" />
                ) : (
                  <Select value={form.study_program} onValueChange={handleProgramChange} disabled={!form.faculty}>
                    <SelectTrigger className="h-12" data-testid="select-program"><SelectValue placeholder={t("form.q.program_placeholder")} /></SelectTrigger>
                    <SelectContent>
                      {programOptions.map((p) => <SelectItem key={p} value={p}>{valueLabel(p)}</SelectItem>)}
                      <SelectItem value="__other__">{t("form.q.other_specify")}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <RadioField label={t("form.q.degree_level")} required options={["Bachelor", "Master"]} value={form.degree_level} onChange={(v) => set("degree_level", v)} testid="radio-degree" />
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label={t("form.q.semesters_studied")} required><Input value={form.semesters_studied} onChange={(e) => set("semesters_studied", e.target.value)} data-testid="input-semesters" className="h-12" /></Field>
                <Field label={t("fields.program_duration")} required><Input value={form.program_duration} onChange={(e) => set("program_duration", e.target.value)} data-testid="input-duration" className="h-12" /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label={t("fields.remaining_exams")} required><Input value={form.remaining_exams} onChange={(e) => set("remaining_exams", e.target.value)} data-testid="input-remaining" className="h-12" /></Field>
                <Field label={t("fields.gpa")} required><Input value={form.gpa} onChange={(e) => set("gpa", e.target.value)} data-testid="input-gpa" className="h-12" /></Field>
              </div>
              <RadioField label={t("form.q.financial_support")} required options={["Yes", "No"]} value={form.financial_support} onChange={(v) => setForm((p) => ({ ...p, financial_support: v, financial_support_institution: v === "Yes" ? p.financial_support_institution : "" }))} testid="radio-support" />
              {form.financial_support === "Yes" && (
                <Field label={t("form.q.financial_support_institution")} required><Input value={form.financial_support_institution} onChange={(e) => set("financial_support_institution", e.target.value)} data-testid="input-support-institution" className="h-12" /></Field>
              )}
              <RadioField label={t("form.q.previous_penda")} required options={["Yes", "No"]} value={form.previous_penda} onChange={(v) => set("previous_penda", v)} testid="radio-previous-penda" />
            </div>
          )}

          {step === 2 && (
            <div className="grid gap-6">
              <RadioField label={t("fields.parents_marital_status")} required options={["Married", "Widowed", "Divorced"]} value={form.parents_marital_status} onChange={(v) => set("parents_marital_status", v)} testid="radio-marital" />
              <RadioField label={t("form.q.siblings")} required options={["0", "1", "2", "3+"]} value={form.siblings} onChange={(v) => set("siblings", v)} testid="radio-siblings" />
              <RadioField label={t("form.q.parent_unemployed")} required options={["Father", "Mother", "Unemployed (both)", "Employed (both)"]} value={form.parent_unemployed} onChange={(v) => set("parent_unemployed", v)} testid="radio-unemployed" />
              <Field label={t("form.q.monthly_income")} required><Input value={form.monthly_income} onChange={(e) => set("monthly_income", e.target.value)} data-testid="input-income" className="h-12" /></Field>
              <RadioField label={t("form.q.vehicles")} required options={["0", "1", "2", "3+"]} value={form.vehicles} onChange={(v) => set("vehicles", v)} testid="radio-vehicles" />
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-6">
              {DOCUMENT_FIELDS.map((doc) => (
                REFERENCE_SLOTS.includes(doc.key)
                  ? <RecommendationField key={doc.key} doc={doc} files={files} setFiles={setFiles}
                      reference={referenceFor(doc.key)} setReferences={setReferences} />
                  : <FileUploadField key={doc.key} doc={doc} files={files} setFiles={setFiles} />
              ))}
            </div>
          )}

          {step === 4 && (
            <div className="grid gap-6">
              <Field label={t("form.q.how_heard")} required>
                <div className="flex flex-col gap-3">
                  {HOW_HEARD.map((o) => (
                    <label key={o} className="flex items-center gap-3 cursor-pointer">
                      <Checkbox checked={form.how_heard.includes(o)} onCheckedChange={() => toggleHeard(o)} />
                      <span className="text-sm">{valueLabel(o)}</span>
                    </label>
                  ))}
                </div>
              </Field>
              <Field label={t("form.q.notes")}>
                <Textarea value={form.additional_notes} onChange={(e) => set("additional_notes", e.target.value)} rows={4} />
              </Field>
              <label className="flex items-start gap-3 cursor-pointer bg-secondary/50 border border-primary/10 rounded-lg p-4">
                <Checkbox checked={form.consent === "I agree"} onCheckedChange={(c) => set("consent", c ? "I agree" : "")} data-testid="checkbox-consent" className="mt-1" />
                <span className="text-sm leading-relaxed">{t("form.q.consent")}</span>
              </label>
            </div>
          )}

          <div className="flex items-center justify-between mt-10 pt-6 border-t border-border">
            <Button variant="ghost" onClick={prev} disabled={step === 0} data-testid="prev-step-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> {t("common.back")}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={next} data-testid="next-step-btn">{t("form.continue")} <ArrowRight className="w-4 h-4 ml-2" /></Button>
            ) : (
              <Button onClick={submit} disabled={submitting} data-testid="submit-application-btn">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("common.saving")}</> : mode === "edit" ? t("form.save_changes") : t("form.submit")}
              </Button>
            )}
          </div>
        </div>
    </>
  );
}
